/**
 * WAA (Web Animations API) position tracking for marquee tracks.
 *
 * Why WAA instead of CSS @keyframes + animation-play-state:
 * On iOS Safari, getComputedStyle() returns stale transform values after
 * pausing a WAA animation. By managing the animation via the Web Animations
 * API, we can read waAnimation.currentTime to compute the exact pixel position
 * without relying on getComputedStyle. This is critical for drag-to-scrub,
 * where we need the precise position at the moment the user starts dragging.
 *
 * CSS @keyframes still run initially (before WAA kicks in) and serve as the
 * fallback for non-WAA browsers (jsdom, older browsers). The watch in initWAA()
 * converts the CSS animation to WAA as soon as the track element has layout.
 */

import { type Ref, getCurrentScope, watch } from 'vue'

const supportsWAA =
  typeof Element !== 'undefined' &&
  typeof Element.prototype.animate === 'function'

interface UseWaaPositionOptions {
  trackRef: Ref<HTMLElement | null>
  duration: number
}

interface UseWaaPositionReturn {
  position: { current: number }
  readCurrentTranslate: () => number
  setDragTranslate: (translatePx: number) => void
  pauseScroll: () => void
  resumeScroll: () => void
  startScroll: () => void
  cancelWAA: () => void
  initWAA: () => void
  cleanup: () => void
}

export function useWaaPosition(
  options: UseWaaPositionOptions,
): UseWaaPositionReturn {
  const { trackRef, duration } = options

  let waAnimation: Animation | null = null
  let waaStartPx = 0
  let stopWatch: (() => void) | null = null

  const position = { current: 0 }

  function readCurrentTranslate(): number {
    if (waAnimation) {
      const track = trackRef.value
      if (!track) return position.current

      const halfWidth = track.scrollWidth / 2
      if (halfWidth <= 0) return position.current

      const t = Number(waAnimation.currentTime ?? 0)
      const progress = (t % duration) / duration

      return waaStartPx - halfWidth * progress
    }

    const track = trackRef.value
    if (!track) return position.current

    // Inline transform is the most reliable source — it's what we
    // set during drag and during the resume-delay hold period.
    if (track.style.transform && track.style.transform !== '') {
      const match = track.style.transform.match(/translateX\(([^)]+)px\)/)
      if (match) return parseFloat(match[1]) || 0
    }

    // CSS animation is running — fall back to getComputedStyle.
    // This is unreliable on iOS Safari after pausing a WAA animation,
    // but acceptable here because WAA should normally be active.
    const { transform } = window.getComputedStyle(track)
    if (transform === 'none') return position.current

    const match = transform.match(/matrix(?:3d)?\(([^)]+)\)/)
    if (!match) return position.current

    const values = match[1].split(',')

    return parseFloat(values.length === 6 ? values[4] : values[12]) || 0
  }

  function setDragTranslate(translatePx: number) {
    const track = trackRef.value
    if (!track) return

    const halfWidth = track.scrollWidth / 2
    let next = translatePx
    if (halfWidth > 0) {
      const mod = ((translatePx % halfWidth) + halfWidth) % halfWidth
      next = mod === 0 ? 0 : mod - halfWidth
    }

    position.current = next
    track.style.transform = `translateX(${next}px)`
  }

  function startWAA(fromPx: number) {
    const track = trackRef.value
    if (!track) return

    const halfWidth = track.scrollWidth / 2
    if (halfWidth <= 0) return

    if (waAnimation) {
      waAnimation.cancel()
    }

    track.style.animation = 'none'

    waaStartPx = fromPx
    position.current = fromPx

    waAnimation = track.animate(
      [
        { transform: `translateX(${fromPx}px)` },
        { transform: `translateX(${fromPx - halfWidth}px)` },
      ],
      {
        duration,
        iterations: Infinity,
        easing: 'linear',
      },
    )

    track.style.transform = ''
  }

  function cancelWAA() {
    if (waAnimation) {
      waAnimation.cancel()
      waAnimation = null
    }
  }

  function computeResumeDelay(): number {
    const track = trackRef.value
    if (!track) return 0

    const trackWidth = track.scrollWidth
    if (trackWidth === 0) return 0

    const halfWidth = trackWidth / 2
    const clampedTranslate = Math.max(-halfWidth, Math.min(0, position.current))
    const fraction = -clampedTranslate / halfWidth

    return -(fraction * duration)
  }

  function enableAnimationCSS(fromDelayMs: number) {
    const track = trackRef.value
    if (!track) return
    track.style.animation = ''
    track.style.animationPlayState = 'running'
    track.style.animationDelay = `${fromDelayMs}ms`

    requestAnimationFrame(() => {
      track.style.transform = ''
    })
  }

  function pauseScroll() {
    position.current = readCurrentTranslate()
    if (waAnimation) {
      waAnimation.pause()
    } else {
      const track = trackRef.value
      if (track) track.style.animationPlayState = 'paused'
    }
  }

  function resumeScroll() {
    if (waAnimation) {
      waAnimation.play()
    } else {
      const track = trackRef.value
      if (track) track.style.animationPlayState = 'running'
    }
  }

  function startScroll() {
    if (supportsWAA) {
      startWAA(position.current)
    }
    if (!waAnimation) {
      const delayMs = computeResumeDelay()
      enableAnimationCSS(delayMs)
    }
  }

  function initWAA() {
    if (!supportsWAA || !getCurrentScope()) return

    function tryInit() {
      const track = trackRef.value
      if (!track || waAnimation) return

      // Don't init WAA during drag or resume-delay hold —
      // inline transform is set in those states and starting WAA
      // would clear it, causing a visual jump.
      if (track.style.transform && track.style.transform !== '') return

      const halfWidth = track.scrollWidth / 2
      if (halfWidth <= 0) {
        requestAnimationFrame(tryInit)
        return
      }

      const px = readCurrentTranslate()
      startWAA(px)
    }

    stopWatch = watch(
      trackRef,
      (track) => {
        if (!track || waAnimation) return
        requestAnimationFrame(tryInit)
      },
      { flush: 'post' },
    )
  }

  function cleanup() {
    cancelWAA()
    stopWatch?.()
  }

  return {
    position,
    readCurrentTranslate,
    setDragTranslate,
    pauseScroll,
    resumeScroll,
    startScroll,
    cancelWAA,
    initWAA,
    cleanup,
  }
}
